import { useEffect, useRef, useState } from "react";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { FAQ, JourneyStep } from "../../../types/journey";
import { StepActionButtons } from "../StepActionButtons";
import styles from "./CommonQuestions.module.css";
import { FaqItem } from "./FaqItem";

export interface CommonQuestionsProps {
	faqs: FAQ[];
	step: JourneyStep | null;
	stepId: string;
	onFaqToggle?: (faqId: string, expanded: boolean) => void;
	onAskEve?: (prefill: string) => void;
	onCallNurse?: () => void;
}

export function CommonQuestions({
	faqs,
	step,
	stepId,
	onFaqToggle,
	onAskEve,
	onCallNurse,
}: CommonQuestionsProps) {
	const { t } = useClientTranslations("journey");
	const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);
	const previousStepIdRef = useRef(stepId);

	// D-02: reset accordion when stepId changes (compares against ref to read stepId in body)
	useEffect(() => {
		if (previousStepIdRef.current !== stepId) {
			previousStepIdRef.current = stepId;
			setExpandedFaqId(null);
		}
	}, [stepId]);

	const handleToggle = (faqId: string) => {
		const willExpand = expandedFaqId !== faqId;
		const nextId = willExpand ? faqId : null;
		setExpandedFaqId(nextId);
		if (onFaqToggle) {
			onFaqToggle(faqId, willExpand);
		}
	};

	return (
		<section
			className={styles.section}
			data-step-content="faq"
			aria-labelledby="common-questions-heading"
		>
			<h2 id="common-questions-heading" className={styles.heading}>
				{t("commonQuestions.heading")}
			</h2>

			{faqs.length > 0 ? (
				<ul className={styles.accordion}>
					{faqs.map((faq) => (
						<li key={faq.id} className={styles.accordionItem}>
							<FaqItem
								faq={faq}
								expanded={expandedFaqId === faq.id}
								onToggle={handleToggle}
								expandLabel={t("commonQuestions.expandLabel")}
								collapseLabel={t("commonQuestions.collapseLabel")}
							/>
						</li>
					))}
				</ul>
			) : null}

			<div className={styles.buttonsRow}>
				<StepActionButtons
					step={step}
					onAskEve={onAskEve}
					onCallNurse={onCallNurse}
				/>
			</div>
		</section>
	);
}
